// LP Pricing Pure Functions
// All functions are pure — no side effects, no state mutation.

import { MARGIN_BANDS, SUPPORTED_PAIRS_SET } from './constants';

/**
 * Derive the client rate from the mid rate and margin.
 * For buy-side (bank buys base), client rate = mid - margin.
 * For sell-side (bank sells base), client rate = mid + margin.
 * @param {number} mid - Mid-market rate
 * @param {number} margin - Margin in rate points (e.g. 0.50 for KES)
 * @param {'buy'|'sell'} side - Bank's side
 * @returns {number} clientRate
 */
export function deriveClientRate(mid, margin, side) {
  if (!mid || !margin) return mid || 0;
  return side === 'buy' ? mid - margin : mid + margin;
}

/**
 * Compute the counter amount given base amount and rate.
 * @param {number} baseAmount
 * @param {number} rate
 * @returns {number}
 */
export function computeCounterAmount(baseAmount, rate) {
  if (!baseAmount || !rate) return 0;
  return Math.round(baseAmount * rate * 100) / 100;
}

/**
 * Evaluate pre-trade checks for an RFQ against counterparty and book state.
 * Returns an array of { check, passed, message, blocking }.
 */
export function evaluatePreTradeChecks({
  rfq,
  counterparty,
  bookContext,
  dealerLimits,
  priceFeed,
  requiredDocs = [],
}) {
  const checks = [];

  // 1. Counterparty KYC status
  const kycOk = counterparty?.kycStatus === 'verified';
  checks.push({
    check: 'KYC Status',
    passed: kycOk,
    message: kycOk ? 'Counterparty KYC verified' : `Counterparty KYC: ${counterparty?.kycStatus || 'unknown'}`,
    blocking: !kycOk,
  });

  // 2. Counterparty not suspended
  const notSuspended = counterparty?.kycStatus !== 'suspended' && counterparty?.kycStatus !== 'prohibited';
  checks.push({
    check: 'Counterparty Active',
    passed: notSuspended,
    message: notSuspended ? 'Counterparty active' : 'Counterparty is suspended or prohibited',
    blocking: !notSuspended,
  });

  // 3. Counterparty limit
  if (dealerLimits?.counterpartyLimits && counterparty?.id) {
    const limit = dealerLimits.counterpartyLimits[counterparty.id];
    if (limit) {
      const withinLimit = (limit.used + (rfq?.amount || 0)) <= limit.max;
      checks.push({
        check: 'Counterparty Limit',
        passed: withinLimit,
        message: withinLimit
          ? `Within limit (${formatNum(limit.used + (rfq?.amount || 0))}/${formatNum(limit.max)})`
          : `Exceeds counterparty limit (${formatNum(limit.used + (rfq?.amount || 0))}/${formatNum(limit.max)})`,
        blocking: !withinLimit,
      });
    }
  }

  // 4. Currency pair supported
  const pairSupported = SUPPORTED_PAIRS_SET.has(rfq?.currencyPair);
  checks.push({
    check: 'Pair Supported',
    passed: pairSupported,
    message: pairSupported ? `${rfq?.currencyPair} supported` : `${rfq?.currencyPair} not supported`,
    blocking: !pairSupported,
  });

  // 5. Price feed freshness
  const feedEntry = priceFeed?.[rfq?.currencyPair];
  const feedFresh = feedEntry && (Date.now() - feedEntry.timestamp) < 10000;
  checks.push({
    check: 'Price Feed',
    passed: feedFresh,
    message: feedFresh ? 'Price feed live' : 'Price feed stale or unavailable',
    blocking: false, // warning, not blocking
  });

  // 6. NOP headroom
  if (bookContext?.nopUtilization != null) {
    const nopOk = bookContext.nopUtilization < 0.95;
    checks.push({
      check: 'NOP Headroom',
      passed: nopOk,
      message: nopOk
        ? `NOP at ${(bookContext.nopUtilization * 100).toFixed(0)}%`
        : `NOP near limit (${(bookContext.nopUtilization * 100).toFixed(0)}%)`,
      blocking: bookContext.nopUtilization >= 1.0,
    });
  }

  // 7. Required documents
  if (requiredDocs.length > 0) {
    const counterpartyDocs = new Set(counterparty?.documents?.map(d => d.type) || []);
    const missingDocs = requiredDocs.filter(d => !counterpartyDocs.has(d.type));
    const docsOk = missingDocs.length === 0;
    checks.push({
      check: 'Documentation',
      passed: docsOk,
      message: docsOk
        ? 'All required documents present'
        : `Missing: ${missingDocs.map(d => d.label).join(', ')}`,
      blocking: missingDocs.some(d => d.required),
    });
  }

  return checks;
}

/**
 * Check if a margin is within the allowed band for a given amount.
 * @returns {{ inBand: boolean, band: object, marginPct: number }}
 */
export function checkMarginBand(amount, marginPct) {
  let band;
  if (amount <= MARGIN_BANDS.SMALL.maxAmount) {
    band = MARGIN_BANDS.SMALL;
  } else if (amount <= MARGIN_BANDS.MEDIUM.maxAmount) {
    band = MARGIN_BANDS.MEDIUM;
  } else {
    band = MARGIN_BANDS.LARGE;
  }

  const inBand = marginPct >= band.minMargin && marginPct <= band.maxMargin;
  return { inBand, band, marginPct };
}

/**
 * Compute how far the bank's quote was from the winning quote (competitive context).
 * Positive = more expensive for client, negative = cheaper.
 */
export function computeWinningDistance(ourRate, winningRate, side) {
  if (!ourRate || !winningRate) return null;
  // For buy-side (bank buys from client): lower rate = better for bank, worse for client
  // For sell-side (bank sells to client): higher rate = worse for client
  if (side === 'sell') {
    return ourRate - winningRate; // positive = we were more expensive
  }
  return winningRate - ourRate; // positive = we paid less
}

/**
 * Validate that a value date is a valid business day and within settlement window.
 * Simplified check — doesn't use a holiday calendar.
 */
export function validateValueDate(valueDate, settlementDate) {
  if (!valueDate) return { valid: false, reason: 'Value date is required.' };
  const vd = new Date(valueDate);
  const dayOfWeek = vd.getDay();
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return { valid: false, reason: 'Value date falls on a weekend.' };
  }
  if (settlementDate) {
    const sd = new Date(settlementDate);
    if (vd > sd) {
      return { valid: false, reason: 'Value date is after settlement date.' };
    }
  }
  return { valid: true };
}

/**
 * Compute the Net Open Position (NOP) contribution of a proposed trade.
 * @param {number} amount - Base currency amount
 * @param {'buy'|'sell'} side
 * @param {object} currentPosition - { long, short, limit }
 * @returns {{ newLong, newShort, newNet, newUtilization }}
 */
export function computeNopContribution(amount, side, currentPosition) {
  const { long = 0, short = 0, limit = Infinity } = currentPosition || {};
  const newLong = side === 'buy' ? long + amount : long;
  const newShort = side === 'sell' ? short + amount : short;
  const newNet = Math.abs(newLong - newShort);
  const newUtilization = limit > 0 ? newNet / limit : 0;
  return { newLong, newShort, newNet, newUtilization };
}

/**
 * Match an auto-quote rule against an RFQ.
 * @param {object} rule - { pair, minAmount, maxAmount, maxMargin, tier, enabled }
 * @param {object} rfq - { currencyPair, amount, counterpartyTier }
 * @returns {boolean}
 */
export function matchAutoRule(rule, rfq) {
  if (!rule.enabled) return false;
  if (rule.pair !== '*' && rule.pair !== rfq.currencyPair) return false;
  if (rfq.amount < rule.minAmount || rfq.amount > rule.maxAmount) return false;
  if (rule.tier && rule.tier !== '*' && rule.tier !== rfq.counterpartyTier) return false;
  return true;
}

/**
 * Compute aggregate allocation from multiple sub-allocations.
 * Used for aggregate RFQ groups where the total is split across investors.
 */
export function computeAggregateAllocation(allocations) {
  const total = allocations.reduce((sum, a) => sum + (a.amount || 0), 0);
  return allocations.map(a => ({
    ...a,
    pct: total > 0 ? ((a.amount / total) * 100).toFixed(2) : '0.00',
  }));
}

// ─── Internal Helpers ───────────────────────────────────────────────────

function formatNum(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return String(n);
}
